// src/pages/NonUsage.js
import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { format, subDays } from 'date-fns';

export default function NonUsage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRep, setFilterRep] = useState('');

  useEffect(() => {
    async function load() {
      const snap = await getDocs(collection(db, 'orders'));
      setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }
    load();
  }, []);

  function isNonUsage(o) {
    // Explicit flag from new imports
    if (o.hasUsage === true) return false;
    if (o.hasUsage === false) return true;

    // For existing orders without the flag — check date field
    // Bad/blank dates mean no usage confirmed
    if (!o.date) return true;
    const dateStr = String(o.date).slice(0, 10);
    const badDates = ['', '1901-01-01', '1900-01-01', '1900-01-02', '2000-01-02'];
    if (badDates.includes(dateStr)) return true;

    // Date looks like 1/1/1900 range (Excel epoch artifacts)
    if (dateStr && dateStr < '2020-01-01') return true;

    return false;
  }

  // Non-usage = completed installs with no confirmed usage
  const nonUsage = orders.filter(o => {
    if (o.status !== 'Completed' && o.status !== 'Written' && o.status !== 'Processing') return false;
    return isNonUsage(o);
  });

  const MOBILE_IDS = ['mobile_line1', 'mobile_lines2to10', 'mobile_line1_ild', 'mobile_lines2to10_ild'];
  const coreNonUsage   = nonUsage.filter(o => !MOBILE_IDS.includes(o.productId));
  const mobileNonUsage = nonUsage.filter(o => MOBILE_IDS.includes(o.productId));

  const reps = [...new Set(nonUsage.map(o => o.repName).filter(Boolean))].sort();
  const [filterType, setFilterType] = useState('all');

  const filtered = nonUsage.filter(o => {
    const q = search.toLowerCase();
    const isMobile = MOBILE_IDS.includes(o.productId);
    return (!filterRep || o.repName === filterRep) &&
      (filterType === 'all' || (filterType === 'internet' && !isMobile) || (filterType === 'mobile' && isMobile)) &&
      (!q || o.customerName?.toLowerCase().includes(q) || o.repName?.toLowerCase().includes(q));
  });

  function exportCSV() {
    const lines = ['Customer,Product,Rep,Agent Code,Status,Account Number,Work Order'];
    filtered.forEach(o => lines.push(`"${o.customerName}","${o.productName}","${o.repName}","${o.agentCode || ''}","${o.status}","${o.accountNumber || ''}","${o.workOrderNumber || ''}"`));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `non_usage_${format(new Date(), 'yyyy-MM-dd')}.csv`; a.click();
  }

  if (loading) return <div style={{ padding: 60, color: '#555' }}>Loading...</div>;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Non-usage</h1>
          <p style={S.sub}>{nonUsage.length} orders with no confirmed usage · {coreNonUsage.length} internet · {mobileNonUsage.length} mobile</p>
        </div>
        <button style={S.exportBtn} onClick={exportCSV}>Export CSV</button>
      </div>

      <div style={S.infoBox}>
        Orders where no Internet Usage Date (core) or Network Activation (mobile) has been confirmed. Includes completed, written, and processing orders. Commission may be held until usage is confirmed.
      </div>

      <div style={S.toolbar}>
        <input style={S.input} placeholder="Search customer or rep..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={S.select} value={filterRep} onChange={e => setFilterRep(e.target.value)}>
          <option value="">All reps</option>
          {reps.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select style={S.select} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="all">All types</option>
          <option value="internet">Internet only</option>
          <option value="mobile">Mobile only</option>
        </select>
        <div style={S.countBadge}>{filtered.length} orders</div>
      </div>

      <div style={S.tableCard}>
        <table style={S.table}>
          <thead>
            <tr>{['Customer', 'Product', 'Rep', 'Agent code', 'Account #', 'Work order', 'Status'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.map(o => (
              <tr key={o.id}>
                <td style={S.td}>{o.customerName}</td>
                <td style={{ ...S.td, color: '#888' }}>{o.productName}</td>
                <td style={S.td}>{o.repName || <span style={{ color: '#f5a623' }}>Unassigned</span>}</td>
                <td style={{ ...S.td, color: '#00d4a0' }}>{o.agentCode || '—'}</td>
                <td style={{ ...S.td, color: '#555', fontSize: 11 }}>{o.accountNumber || '—'}</td>
                <td style={{ ...S.td, color: '#555', fontSize: 11 }}>{o.workOrderNumber || '—'}</td>
                <td style={S.td}>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5, background: '#1a2e24', color: '#00d4a0' }}>
                    Installed — no usage
                  </span>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', color: '#444', padding: 32 }}>
                {nonUsage.length === 0 ? 'No non-usage orders found' : 'No results match your search'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const S = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  title: { fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 12, color: '#555', margin: '4px 0 0' },
  exportBtn: { background: '#00d4a0', border: 'none', borderRadius: 8, padding: '8px 16px', color: '#000', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  infoBox: { background: '#1a1a10', border: '1px solid #3a3a20', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#888', marginBottom: 16, lineHeight: 1.6 },
  toolbar: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 },
  input: { background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 13, outline: 'none', width: 260 },
  select: { background: '#13131a', border: '1px solid #2a2a3a', borderRadius: 8, padding: '8px 12px', color: '#ccc', fontSize: 12, outline: 'none' },
  countBadge: { fontSize: 12, color: '#555', background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 6, padding: '6px 12px' },
  tableCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 10, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #1e1e2a' },
  td: { padding: '10px 12px', fontSize: 13, color: '#ccc', borderBottom: '1px solid #111' },
};
